// quantiix-shell/src/components/ShellBreadcrumbs.jsx
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Breadcrumb } from '@quantiix/design-system';
import {
    useDataLayer,
    useCachedHierarchy,
    findMachine,
    findOU,
    findFactory
} from '@quantiix/data-layer';
import { ROUTES, parseCurrentRoute } from '../config/routes';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '@/utils/logger.js';

const logger = createLogger('Shell::ShellBreadcrumbs');

// Entities that keep their breadcrumbs even when reached from the navbar.
const NAVBAR_BREADCRUMB_ENTITIES = ['company', 'publicview', 'machine'];

function ShellBreadcrumbs() {
    const navigate = useNavigate();
    const location = useLocation();
    const { companyId, navigationSource, setScope } = useDataLayer();
    const hierarchy = useCachedHierarchy(companyId);
    const { isSuperuser, hasAssignedCompany, primaryCompany } = useAuth();

    const pathname = location.pathname;

    logger.info('Render', {
        pathname,
        companyId,
        navigationSource,
        hasHierarchy: !!hierarchy
    });

    // Parse route - now includes source
    const { entityType, entityId, source } = useMemo(
        () => parseCurrentRoute(pathname),
        [pathname]
    );

    // Climbing OUT of a navbar-opened app ends navbar-entry mode: from there the
    // user is walking the dashboard hierarchy, and those levels must keep their
    // crumbs instead of hitting the navbar gate below.
    //
    // Crumbs that stay inside a navbar app keep the flag — the OU crumb of a
    // navbar-opened Machine leads to that OU's PublicView, and clearing it there
    // would hand the navbar highlight to Dashboard while the user is looking at
    // a PublicView.
    // Declared above the early returns — it's a hook, so it can't be conditional.
    const handleNavigate = useCallback((href) => {
        const { entityType: targetType } = parseCurrentRoute(href);
        const leavesNavbarApp = targetType !== 'publicview' && targetType !== 'machine';

        if (navigationSource === 'navbar' && leavesNavbarApp) {
            setScope({ navigationSource: null });
        }
        navigate(href);
    }, [navigate, navigationSource, setScope]);

    // Early exit - companies list doesn't need breadcrumbs
    if (entityType === 'companies') {
        return null;
    }

    // Navbar navigation hides breadcrumbs (except for company level, and for
    // PublicView/Machine — those land deep in the hierarchy, so the trail is
    // the only way back up; the navbar sets the full scope before navigating).
    // Note: Config MFE doesn't use navbar navigation, so this won't affect it
    if (navigationSource === 'navbar' && !NAVBAR_BREADCRUMB_ENTITIES.includes(entityType)) {
        return null;
    }

    if (!hierarchy || !entityType) {
        return null;
    }

    // Derive context from hierarchy (unchanged logic)
    const context = (() => {
        switch (entityType) {
            case 'machine':
                return findMachine(hierarchy, entityId);
            case 'ou':
            case 'publicview':
                return findOU(hierarchy, entityId);
            case 'factory':
                return findFactory(hierarchy, entityId);
            case 'company':
                return { company: { id: hierarchy.id, name: hierarchy.name } };
            default:
                return null;
        }
    })();

    if (!context) {
        return null;
    }

    // The "Dashboard" crumb must land where the navbar logo lands: a superuser
    // (or a user with no assigned company) goes to the company list; a regular
    // single-company user goes straight to their company — never the empty
    // superuser company-list view. Mirrors HomePage's redirect logic.
    const dashboardHomeHref = (isSuperuser || !hasAssignedCompany || !primaryCompany?.id)
        ? ROUTES.companies()
        : ROUTES.factories(primaryCompany.id);

    // Build hrefs based on source
    const hrefs = buildHrefs(context, source, dashboardHomeHref);

    logger.info('Built hrefs', {
        source,
        entityType,
        hrefs,
        context: {
            company: context.company?.name,
            factory: context.factory?.name,
            ou: context.ou?.name
        }
    });

    return (
        <Breadcrumb
            companyName={context.company?.name}
            factoryName={context.factory?.name}
            ouName={context.ou?.name}
            machineName={context.machine?.name}
            {...hrefs}
            onNavigate={handleNavigate}
        />
    );
}

/**
 * Build breadcrumb hrefs based on source MFE
 * Extracted to keep main component clean
 */
function buildHrefs(context, source, dashboardHomeHref) {
    // Config MFE uses different routes
    if (source === 'config') {
        return {
            dashboardHref: ROUTES.config.companies(),
            companyHref: context.company
                ? ROUTES.config.factories(context.company.id)
                : undefined,
            factoryHref: context.factory
                ? ROUTES.config.ous(context.factory.id)
                : undefined,
            ouHref: context.ou
                ? ROUTES.config.publicviews(context.ou.id)
                : undefined,
        };
    }

    // Default: Dashboard/PublicView/Machine routes (existing behavior)
    return {
        dashboardHref: dashboardHomeHref,
        companyHref: context.company
            ? ROUTES.factories(context.company.id)
            : undefined,
        factoryHref: context.factory
            ? ROUTES.ous(context.factory.id)
            : undefined,
        ouHref: context.ou
            ? ROUTES.publicview(context.ou.id)
            : undefined,
    };
}

export default ShellBreadcrumbs;